import React, { useEffect, useRef, useState } from "react";
import {
  Modal,
  Form,
  Row,
  Col,
  Input,
  Select,
  Button,
  Space,
  message,
  Spin,
  DatePicker,
  ConfigProvider,
  Divider,
  Tabs,
} from "antd";
import dayjs from "dayjs";
import { HomeOutlined, ProfileOutlined } from "@ant-design/icons";
import api from "@/utils/api";
import http from "@/utils/http";
import commonFn from "@/utils/commonFn";
import { CustomUploadNew1 } from "@/hocComponents";

const UpdateModal = ({ open, onCancel, record, onSuccess }) => {
  const [form] = Form.useForm();
  const customUploadNewRef = useRef([]);

  const [btnDisabled, setBtnDisabled] = useState(false);
  const [vendorTypes, setVendorTypes] = useState([]);
  const [glAcctTypes, setGlAcctTypes] = useState([]);
  const [states, setStates] = useState([]);
  const [btnLoading, setBtnLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [filePaths, setFilePaths] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTabKey, setActiveTabKey] = useState("w9");

  const validateMessages = {
    required: "${label} is required!",
  };

  const isIfList = [
    { value: "Y", label: "Yes" },
    { value: "N", label: "No" },
  ];

  const isACHList = [
    { value: "1", label: "Yes" },
    { value: "0", label: "No" },
  ];

  const closeModal = () => {
    onCancel?.();
  };

  const handleCancel = () => {
    form.resetFields();
    setFilePaths([]);
    setLoading(false);
    setLoadingData(false);
    setActiveTabKey("w9");
    closeModal();
  };

  const fetchInitialData = async () => {
    try {
      const [vendorTypesResponse, statesResponse, glAcctResponse] =
        await Promise.all([
          http.get(api.getVendorType, { params: { flag: "status" } }),
          http.get(api.getVendorType, { params: { flag: "state" } }),
          http.get(api.getVendorType, { params: { flag: "glAcct" } }),
        ]);

      setVendorTypes(
        (vendorTypesResponse || []).map((item, index) => ({
          value: item.typeCode,
          label: item.typeName,
          key: `${item.typeCode}-${index}`,
        })),
      );

      setStates(
        (statesResponse || []).map((item, index) => ({
          value: item.stateM,
          label: item.stateName,
          key: `${item.stateM}-${index}`,
        })),
      );

      setGlAcctTypes(
        (glAcctResponse || []).map((item, index) => ({
          value: item.glAcct,
          label: item.glAcctName,
          key: `${item.glAcct}-${index}`,
        })),
      );
    } catch (error) {
      console.error("Failed to load vendor dropdown data:", error);
    }
  };

  const fetchVendorDetail = async (vendorId) => {
    try {
      setLoadingData(true);
      const res = await http.get(api.getVendorData, {
        params: { id: vendorId },
      });
      if (res.code === "1") {
        const data = res.data;
        form.setFieldsValue({
          vendorcode: data.vendid,
          vendorname: data.name,
          vendortype: data.vendorType,
          // monthlySalary: data.monthlySalary,
          remarks: data.remarks,
          street: data.street,
          city: data.city,
          state: data.state,
          zip: data.zip,
          stateM: data.stateM,
          cityM: data.cityM,
          streetM: data.streetM,
          zipM: data.zipM,
          addressO: data.streetO,
          cityO: data.cityO,
          stateO: data.stateO,
          zipO: data.zipO,
          name: data.nameKey,
          work: data.phoneWorkNumber ?? data.phoneWork,
          extension: data.ext,
          mobile: data.phoneNumber ?? data.phone,
          fax: data.faxNumber ?? data.fax,
          email: data.email,
          website: data.web,
          webUser: data.webUser,
          webPwd: data.webPwd,
          webLogin: data.webLogin,
          flg1099: data.flg1099,
          fedId: data.fedId,
          ach: String(data.ach ?? ""),
          routingNumber: data.routingNumber,
          accountNumber: data.accountNumber,
          glAcct: data.glAcct,
          vendorId,
          sysDate: data?.sysDate ? dayjs(data.sysDate) : null,
          lastIssueCheck: data?.lastIssueCheck
            ? dayjs(data.lastIssueCheck)
            : null,
          udate: data?.udate ? dayjs(data.udate) : null,
          usid: data.usid,
          status: data.status,
          hireDate: data?.hireDate ? dayjs(data.hireDate) : null,
        });
      } else {
        message.error(res.msg);
      }
    } catch (error) {
      console.error("Failed to fetch vendor detail:", error);
      message.error(error.message);
    } finally {
      setLoadingData(false);
    }
  };

  const getFilePathFn = async () => {
    try {
      setLoading(true);
      const types = ["w9", "insurance_certificate", "contracts", "vendors"];
      const responses = await Promise.all(
        types.map((type) =>
          http.get(api.getFilePaths, {
            params: { id: record?.vendorId, type },
          }),
        ),
      );

      const paramsArr = responses.flatMap((res, idx) => {
        const data = res?.data || [];
        return data.map((file) => ({
          type: types[idx],
          customType: types[idx],
          uuid: file.uuid,
          filePath: file.filePath,
          path: file.path,
          ext: file.ext,
          name: file.name,
          uid: file.uuid,
        }));
      });

      if (paramsArr.length > 0) {
        const pdfRes = await http.post(api.getVendorPDF, paramsArr);
        const pdfArr = pdfRes.data || [];
        const newFilePaths = paramsArr.map((file, idx) => ({
          ...file,
          base64: pdfArr[idx]?.base64 || null,
          allPath: pdfArr[idx]?.base64 || null,
        }));
        setFilePaths(newFilePaths);
      } else {
        setFilePaths([]);
      }
    } catch (err) {
      console.error("Failed to load files", err);
    } finally {
      setLoading(false);
    }
  };

  const getUploadFilePaths = (name) => {
    const currentFiles = filePaths.filter((item) => item.customType === name);
    if (currentFiles.length > 0) {
      currentFiles[-1] = currentFiles[0];
    }
    return currentFiles;
  };

  const uploadTabsChildrenDoc = (name) => {
    return (
      <Form.Item
        labelCol={{ span: 0 }}
        wrapperCol={{ span: 24 }}
        name={name}
        label={null}
      >
        <>
          <div style={{ height: "50px" }}></div>
          <CustomUploadNew1
            ref={(el) => (customUploadNewRef.current[name] = el)}
            customConfig={{
              uploadButtonStyle: {
                top: "0",
              },
              pdfHeight: "62vh",
              fileFullName: "name",
              notMultiple: true,
              accept: ".jpg, .jpeg, .png, .pdf",
              status: "add",
              noUploadBtn: true,
            }}
            setFilePaths={setFilePaths}
            filePaths={getUploadFilePaths(name)}
            setBtnDisabled={setBtnDisabled}
            btnDisabled={btnDisabled}
          />
        </>
      </Form.Item>
    );
  };

  const uploadTabsDoc = () => {
    const items = [
      {
        key: "w9",
        label: "Upload W-9 Documents",
        forceRender: true,
        children: uploadTabsChildrenDoc("w9"),
      },
      {
        key: "insurance_certificate",
        label: "Upload Insurance Certificate",
        forceRender: true,
        children: uploadTabsChildrenDoc("insurance_certificate"),
      },
      {
        key: "contracts",
        label: "Upload Contracts",
        forceRender: true,
        children: uploadTabsChildrenDoc("contracts"),
      },
      {
        key: "vendors",
        label: "Upload Vendors",
        forceRender: true,
        children: uploadTabsChildrenDoc("vendors"),
      },
    ];

    return (
      <Tabs activeKey={activeTabKey} onChange={setActiveTabKey} items={items} />
    );
  };

  const submitFn = async () => {
    try {
      const values = await form.validateFields();
      const uidList = (filePaths || [])
        .map((f) => f.uuid || f.uid)
        .filter(Boolean);
      const params = {
        vendorId: record.vendorId,
        vendid: values?.vendorcode || undefined,
        name: values?.vendorname || undefined,
        vendorType: values?.vendortype || undefined,
        // monthlySalary: values?.monthlySalary || undefined,
        remarks: values?.remarks || undefined,
        street: values?.street || undefined,
        city: values?.city || undefined,
        state: values?.state || undefined,
        zip: values?.zip || undefined,
        streetM: values?.streetM || undefined,
        cityM: values?.cityM || undefined,
        stateM: values?.stateM || undefined,
        zipM: values?.zipM || undefined,
        streetO: values?.streetO || undefined,
        cityO: values?.cityO || undefined,
        stateO: values?.stateO || undefined,
        zipO: values?.zipO || undefined,
        nameKey: values?.name || undefined,
        phoneWork: values?.work || undefined,
        ext: values?.extension || undefined,
        phone: values?.mobile || undefined,
        fax: values?.fax || undefined,
        email: values?.email || undefined,
        web: values?.website || undefined,
        webUser: values?.webUser || undefined,
        webPwd: values?.webPwd || undefined,
        webLogin: values?.webLogin || undefined,
        flg1099: values?.flg1099 || undefined,
        fedId: values?.fedId || undefined,
        ach: values?.ach || undefined,
        routingNumber: values?.routingNumber || undefined,
        accountNumber: values?.accountNumber || undefined,
        glAcct: values?.glAcct || undefined,
        uuidList: uidList,
        status: values?.status || undefined,
        hireDate: values?.hireDate
        ? values.hireDate.format("YYYY-MM-DD")
       : null,
        lastIssueCheck: values?.lastIssueCheck
          ? values.lastIssueCheck.format("YYYY-MM-DD")
          : null,
      };
      // console.log(params);
      setBtnLoading(true);
      const response = await http.post(api.getVendorUpdate, params);
      if (response.code === "1") {
        message.success("Update successfully.");
        closeModal();
        onSuccess?.();
      } else {
        message.error(response.msg);
      }
    } catch (error) {
      console.error("Failed to submit form:", error);
      message.error("Failed to submit form. Please try again.");
    } finally {
      setBtnLoading(false);
    }
  };

  useEffect(() => {
    if (open && record?.vendorId) {
      form.resetFields();
      fetchInitialData();
      fetchVendorDetail(record.vendorId);
      getFilePathFn();
    }
  }, [open, record, form]);

  const getRequiredRules = (form, extraRules = []) => [
    ({ getFieldValue }) => ({
      required: getFieldValue("status") !== "I",
    }),
    ...extraRules,
  ];

  return (
    <Modal
      title={
        <div style={{ fontSize: "24px", fontWeight: "bold", color: "#3c8dbc" }}>
          <HomeOutlined style={{ marginRight: "8px" }} />
          Update Vendor
          <div
            style={{ borderTop: "3px solid #3c8dbc", marginTop: "8px" }}
          ></div>
        </div>
      }
      className="VendorMWorkerModal"
      centered
      open={open}
      onCancel={closeModal}
      footer={null}
      maskClosable={false}
      afterOpenChange={(flag) => {
        if (!flag) {
          form.resetFields();
          setFilePaths([]);
          setActiveTabKey("w9");
        }
      }}
      width="80%"
    >
      <Spin spinning={loadingData} tip="Loading...">
        <Form
          form={form}
          layout="horizontal"
          labelCol={{ span: 8 }}
          wrapperCol={{ span: 16 }}
          labelWrap={true}
          labelAlign="right"
          initialValues={{
            flg1099: "N",
          }}
          validateMessages={validateMessages}
        >
          <Row gutter={[16, 16]}>
            <Col span={6}>
              <Form.Item name="vendorname" label="Vendor / Company Name :">
                <Input readOnly />
              </Form.Item>
              <Form.Item name="vendorcode" label="Vendor Code :">
                <Input readOnly />
              </Form.Item>

              <ConfigProvider
                componentDisabled={
                  !commonFn.btnAuthorityFlag("EditServiceProvides")
                }
              >
                <div
                  style={{
                    marginBottom: "16px",
                    fontWeight: "bold",
                    fontSize: "20px",
                  }}
                >
                  <ProfileOutlined style={{ marginRight: "8px" }} />
                  Service Provides
                </div>

                <Form.Item
                  name="vendortype"
                  label="Vendor Type"
                  rules={getRequiredRules(form)}
                >
                  <Select
                    showSearch
                    allowClear
                    placeholder="Please Select Vendor Type"
                    optionFilterProp="label"
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    options={vendorTypes}
                  />
                </Form.Item>

                <Form.Item name="hireDate" label="Hire Date">
                  <DatePicker
                    style={{ width: "100%" }}
                    format="YYYY-MM-DD"
                    picker="date"
                    placeholder="YYYY-MM-DD"
                  />
                </Form.Item>

                {/* {commonFn.formInputNumberDoc({
                  name: "monthlySalary",
                  label: "Monthly Salary",
                  form,
                  isNoRequired: true,
                  disabled: !commonFn.btnAuthorityFlag("EditServiceProvides"),
                  validator: (_, value, callback) => {
                    if (value >= 1000000) {
                      callback("The Amount Must Be Between 0 And 1000000.");
                    } else {
                      callback();
                    }
                  },
                })} */}

                <Form.Item name="remarks" label="Remark">
                  <Input placeholder="Please input Remark" />
                </Form.Item>

                <Form.Item
                  name="status"
                  label="Status"
                  rules={[{ required: true }]}
                >
                  <Select
                    showSearch
                    allowClear
                    placeholder="Status"
                    optionFilterProp="label"
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    options={[
                      { value: "A", label: "Active" },
                      { value: "I", label: "Inactive" },
                    ]}
                    onChange={() => {
                      form.validateFields([
                        "vendortype",
                        "street",
                        "city",
                        "state",
                        //  "zip",
                        "flg1099",
                      ]);
                    }}
                  />
                </Form.Item>

                <Form.Item name="sysDate" label="Since">
                  <DatePicker
                    readOnly
                    style={{ width: "100%" }}
                    format="MM/DD/YYYY"
                    placeholder="YYYY-MM-DD"
                  />
                </Form.Item>

                <Form.Item name="lastIssueCheck" label="Last lssue Check">
                  <DatePicker
                    readOnly
                    style={{ width: "100%" }}
                    format="MM/DD/YYYY"
                  />
                </Form.Item>
              </ConfigProvider>

              <ConfigProvider
                componentDisabled={
                  !commonFn.btnAuthorityFlag("EditVendorContactInfo")
                }
              >
                <div
                  style={{
                    marginBottom: "16px",
                    fontWeight: "bold",
                    fontSize: "20px",
                  }}
                >
                  <ProfileOutlined style={{ marginRight: "8px" }} />
                  Mailing Address (This will print on check.)
                </div>

                <Form.Item
                  name="street"
                  label="Address"
                  rules={getRequiredRules(form)}
                >
                  <Input />
                </Form.Item>

                <Form.Item
                  name="city"
                  label="City"
                  rules={getRequiredRules(form)}
                >
                  <Input />
                </Form.Item>

                <Form.Item
                  name="state"
                  label="State"
                  rules={getRequiredRules(form)}
                >
                  <Select
                    showSearch
                    allowClear
                    placeholder="State"
                    optionFilterProp="label"
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    options={states}
                  />
                </Form.Item>

                <Form.Item
                  name="zip"
                  label="Zip Code"
                  dependencies={["status"]}
                  rules={[
                    ({ getFieldValue }) => ({
                      required: getFieldValue("status") === "A",
                      validator(_, value) {
                        const status = getFieldValue("status");
                        const v = (value || "").trim();
                        // Active：必填
                        if (status === "A" && !v) {
                          return Promise.reject(
                            new Error("Zip Code is required"),
                          );
                        }
                        // Inactive：非必填
                        if (!v) return Promise.resolve();
                        // 格式校验
                        const zipRegex = /^\d{5}(-\d{4})?$/;
                        if (!zipRegex.test(v)) {
                          return Promise.reject(
                            new Error(
                              "Please enter a valid US Zip Code (12345 or 12345-6789)",
                            ),
                          );
                        }

                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <Input />
                </Form.Item>

                <div
                  style={{
                    marginBottom: "16px",
                    fontWeight: "bold",
                    fontSize: "20px",
                  }}
                >
                  <ProfileOutlined style={{ marginRight: "8px" }} />
                  Physical Address
                </div>

                <Form.Item name="streetM" label="Address(P.O. Box)">
                  <Input />
                </Form.Item>

                <Form.Item name="cityM" label="City">
                  <Input />
                </Form.Item>

                <Form.Item name="stateM" label="State">
                  <Select
                    showSearch
                    allowClear
                    placeholder="State"
                    optionFilterProp="label"
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    options={states}
                  />
                </Form.Item>

                <Form.Item
                  name="zipM"
                  label="Zip Code"
                  rules={[{ validator: commonFn.zipValidator }]}
                >
                  <Input />
                </Form.Item>

                <div
                  style={{
                    marginBottom: "16px",
                    fontWeight: "bold",
                    fontSize: "20px",
                  }}
                >
                  <ProfileOutlined style={{ marginRight: "8px" }} />
                  Overnight Mailing Info
                </div>

                <Form.Item name="addressO" label="Address">
                  <Input />
                </Form.Item>

                <Form.Item name="cityO" label="City">
                  <Input />
                </Form.Item>

                <Form.Item name="stateO" label="State">
                  <Select
                    showSearch
                    allowClear
                    placeholder="State"
                    optionFilterProp="label"
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    options={states}
                  />
                </Form.Item>

                <Form.Item
                  name="zipO"
                  label="Zip Code"
                  rules={[{ validator: commonFn.zipValidator }]}
                >
                  <Input />
                </Form.Item>
              </ConfigProvider>
            </Col>

            <Col span={6}>
              <ConfigProvider
                componentDisabled={
                  !commonFn.btnAuthorityFlag("EditVendorContactInfo")
                }
              >
                <div
                  style={{
                    marginBottom: "16px",
                    fontWeight: "bold",
                    fontSize: "20px",
                  }}
                >
                  <ProfileOutlined style={{ marginRight: "8px" }} /> Contact
                  Info
                </div>

                <Form.Item name="name" label="Name">
                  <Input />
                </Form.Item>

                <Form.Item
                  name="work"
                  label="Work"
                  rules={[{ validator: commonFn.usPhoneValidator }]}
                >
                  <Input placeholder="e.g. (123) 456-7890" />
                </Form.Item>

                <Form.Item name="extension" label="Extension">
                  <Input />
                </Form.Item>

                <Form.Item
                  name="mobile"
                  label="Mobile"
                  rules={[{ validator: commonFn.usPhoneValidator }]}
                >
                  <Input placeholder="e.g. (123) 456-7890" />
                </Form.Item>

                <Form.Item
                  name="fax"
                  label="Fax"
                  rules={[{ validator: commonFn.usPhoneValidator }]}
                >
                  <Input placeholder="e.g. (123) 456-7890" />
                </Form.Item>

                <Form.Item name="email" label="Email">
                  <Input />
                </Form.Item>

                <Form.Item name="website" label="Website">
                  <Input />
                </Form.Item>

                <Form.Item name="usid" label="Last Updated By">
                  <Input readOnly />
                </Form.Item>

                <Form.Item name="udate" label="Last Updated Date">
                  <DatePicker
                    readOnly
                    style={{ width: "100%" }}
                    format="MM/DD/YYYY"
                  />
                </Form.Item>
              </ConfigProvider>

              <Divider />

              <ConfigProvider
                componentDisabled={
                  !commonFn.btnAuthorityFlag("EditPaymentInfo")
                }
              >
                <div
                  style={{
                    marginBottom: "16px",
                    fontWeight: "bold",
                    fontSize: "20px",
                  }}
                >
                  <ProfileOutlined style={{ marginRight: "8px" }} /> Payment
                  Info
                </div>

                <Form.Item
                  name="flg1099"
                  label="If 1099"
                  rules={getRequiredRules(form)}
                >
                  <Select
                    placeholder="Select "
                    options={isIfList}
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    filterOption={(input, option) =>
                      option.label.toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Form.Item>

                <Form.Item name="fedId" label="Federal ID/SSN">
                  <Input />
                </Form.Item>

                <Form.Item name="ach" label="ACH payment?">
                  <Select
                    placeholder="Select Group"
                    options={isACHList}
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    filterOption={(input, option) =>
                      option.label.toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Form.Item>

                <Form.Item name="routingNumber" label="Routing Number">
                  <Input />
                </Form.Item>

                <Form.Item name="accountNumber" label="Account Number">
                  <Input />
                </Form.Item>

                <Form.Item name="glAcct" label="Default GL Account">
                  <Select
                    placeholder="Select "
                    options={glAcctTypes}
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    filterOption={(input, option) =>
                      option.label.toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Form.Item>

                <Form.Item name="vendorId" noStyle>
                  <Input type="hidden" />
                </Form.Item>
              </ConfigProvider>
            </Col>

            <Col span={12}>
              <div
                style={{
                  marginBottom: "16px",
                  fontWeight: "bold",
                  fontSize: "20px",
                }}
              >
                <HomeOutlined style={{ marginRight: "8px" }} />
                Upload PDF image
              </div>

              <div className="uploadText">
                Uploaded files only support JPG, PNG or PDF.
              </div>
              <Spin spinning={loading} tip="Loading files...">
                {uploadTabsDoc()}
              </Spin>
            </Col>
          </Row>

          <div style={{ textAlign: "center" }}>
            <Space align="center">
              <Button key="back" onClick={closeModal}>
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={submitFn}
                loading={btnLoading}
                disabled={btnDisabled}
              >
                Submit
              </Button>
            </Space>
          </div>
        </Form>
      </Spin>
    </Modal>
  );
};

export default UpdateModal;
